import { List } from 'immutable';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { LiveSiteEventSeverity } from '../../models/status';
import { ActiveLiveSiteEvent } from '../../models/ui/status';
import { FluentSvgIcon } from '../shared/FluentSvgIcon';
import { StatusIndicator } from '../shared/StatusIndicator';
import { getTitleForGeography, isAllGeographiesCollection } from '../../utilities/geographies';
import { getDisplayNameForService, getIconNameForService } from '../../utilities/services';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';
import { isNil } from '../../utilities/typeGuards';

interface StatusCurrentListItemProps {
    geographies: string[];
    impactStartTimestampUtc: string;
    id: number;
    liveSiteEventUrl: (id: number) => string;
    service: string;
    severity: LiveSiteEventSeverity;
    title:  string;
}

export interface StatusCurrentListProps {
    activeLiveSiteEvents: List<ActiveLiveSiteEvent>;
    liveSiteEventUrl: (id: number) => string;

    /**
     * Defaults to true
     */
    useHeader?: boolean;
}

/**
 * Component display details about an active live site event.
 * @param props Component properties
 * @returns Rendered content
 */
export function StatusCurrentListItem (props: StatusCurrentListItemProps) {
    const {
        geographies, id, impactStartTimestampUtc, liveSiteEventUrl, service, severity, title,
    } = props;

    return (
        <div className="status-current-list-item">
            {/* TODO: once public details page is available, make this a hyperlink using the ID. */}
            <div className="status-current-list-item__impact-start-timestamp">
                {formatUtcAsLocalInEnglishLocale(impactStartTimestampUtc)}
            </div>

            <div className="status-current-list-item__title">
                <Link className="status-current-list-item__link ms-Link" to={liveSiteEventUrl(id)}>
                    {title}
                </Link>
            </div>

            <div className="status-current-list-item__service">
                <FluentSvgIcon
                    className="status-current-list-item__service-icon"
                    iconName={getIconNameForService(service)} />

                <span className="status-current-list-item__service-name">
                    {getDisplayNameForService(service)}
                </span>

                <StatusIndicator severity={severity} state="Active" svgStyles={{ width: 16 }} />
            </div>

            <div className="status-current-list-item__geographies">
                {isAllGeographiesCollection(geographies)
                    ? 'All geographies'
                    : geographies.map(geo => getTitleForGeography(geo)).join(', ')}
            </div>
        </div>
    );
}

/**
 * List the top 3 current statuses with severity
 * @param props
 */
export function StatusCurrentList (props: StatusCurrentListProps) {
    const { activeLiveSiteEvents: liveSiteEvents, liveSiteEventUrl } = props;
    const useHeader = isNil(props.useHeader) ? true : props.useHeader;
    const allGood = liveSiteEvents.count() === 0;
    const textStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '24px'
    };

    const cellStyle: React.CSSProperties = {
        display: 'table-cell'
    };

    const iconCellStyle: React.CSSProperties = {
        ...cellStyle,
        width: '35px',
        textAlign: 'left',
        lineHeight: '32px',
        verticalAlign: 'top'
    };

    const rowStyle: React.CSSProperties = {
        display: 'table-row'
    };

    return (
        <>
            {useHeader && <div className="status-text current-incidents__header string-header">Active live site events</div>}

            <div className="status-current-list">
                {allGood && (
                    <div style={rowStyle}>
                        <div style={iconCellStyle}><StatusIndicator state={'Resolved'} severity={undefined} invert={true} /></div>
                        <div style={cellStyle}>
                            <div style={textStyle}>
                                No active events at the moment.
                            </div>
                        </div>
                    </div>
                )}
                {!allGood &&
                    liveSiteEvents.map((liveSiteEvent, index) => {
                        return (
                            <>
                                {index > 0 && <hr className="status-current-list__divider" />}
                                <StatusCurrentListItem
                                    key={index}
                                    liveSiteEventUrl={liveSiteEventUrl}
                                    {...liveSiteEvent} />
                            </>
                        );
                    })
                }
            </div>
        </>
    );
}
